import { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { useLoginProvider } from "../../context/LoginContext";

/** Helpers */
const maskAccount = (n = "") =>
  String(n).replace(/\s+/g, "").replace(/.(?=.{4})/g, "•").replace(/(.{4})/g, "$1 ").trim();

const formatUSD = (amt) =>
  (Number(amt || 0)).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });

const parseISODate = (iso) => {
  if (!iso) return "—";
  try {
    const d = iso.length === 10 ? new Date(`${iso}T00:00:00`) : new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("") || "U";

export default function DashboardTop() {
  const { user } = useLoginProvider();
  const [bankDetails, setBankDetails] = useState(null);
  const [personalInfo, setPersonalInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAccount, setShowAccount] = useState(false); // 👈 state for toggle

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        if (user?.uid) {
          const ref = doc(db, "users", user.uid);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const data = snap.data() || {};

            const p = data.personalInfo || {};
            const addr = data.billingAddress || {};

            if (!mounted) return;

            setPersonalInfo({
              name: p.name || "User",
              email: p.email || "—",
              phone: p.phone || "—",
              dob: data.dob || p.dob || "",
              address: {
                street: addr.street || "—",
                city: addr.city || "—",
                state: addr.state || "—",
                country: addr.country || "—",
                zip: addr.zip || "—",
              },
            });

            setBankDetails({
              accountBalance: data.bankDetails?.accountBalance ?? 0,
              accountNumber: data.bankDetails?.accountNumber || "",
              accountOpeningDate: data.bankDetails?.accountOpeningDate || "",
              accountType: data.bankDetails?.accountType || "Savings",
              mode: data.bankDetails?.mode || "Contactless",
            });
          }
        }
      } catch (e) {
        console.error("Failed to load user data:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [user]);

  const initials = useMemo(() => getInitials(personalInfo?.name), [personalInfo?.name]);

  if (loading) {
    return (
      <div className="dashboard">
        <div className="top-grid">
          <div className="card skeleton" />
          <div className="card skeleton" />
        </div>
      </div>
    );
  }

  if (!bankDetails || !personalInfo) {
    return (
      <div className="dashboard">
        <div className="empty-state">
          <div className="empty-bubble">☁</div>
          <h3>We couldn’t load your profile</h3>
          <p>Please refresh or check your connection.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="top-grid">
        {/* Profile / Personal */}
        <section className="card profile-card">
          <div className="profile-head">
            <div className="avatar">{initials}</div>
            <div className="headline">
              <h2>Hi, {personalInfo.name}</h2>
              <span className="subtle">Welcome back to your dashboard</span>
            </div>
          </div>

          <div className="info-list">
            <div className="item">
              <span className="label">Email</span>
              <span className="value">{personalInfo.email}</span>
            </div>
            <div className="item">
              <span className="label">Phone</span>
              <span className="value">+{personalInfo.phone}</span>
            </div>
            <div className="item">
              <span className="label">Date of Birth</span>
              <span className="value">{parseISODate(personalInfo.dob)}</span>
            </div>
            <div className="item address">
              <span className="label">Address</span>
              <span className="value">
                {personalInfo.address.street}, {personalInfo.address.city},{" "}
                {personalInfo.address.state}, {personalInfo.address.country} —{" "}
                {personalInfo.address.zip}
              </span>
            </div>
          </div>
        </section>

        {/* Bank / Account */}
        <section className="card bank-card">
          <div className="bank-row">
            <div className="chip">Active</div>
            <div className="tags">
              <span className="tag">{bankDetails.accountType}</span>
              <span className="tag">{bankDetails.mode}</span>
            </div>
          </div>

          <div className="balance">
            <div className="balance-label">Available Balance</div>
            <div className="balance-amt">{formatUSD(bankDetails.accountBalance)}</div>
          </div>

          <div className="acc-meta">
            <div className="meta-block">
              <span className="meta-label">Account No.</span>
              <span className="meta-value">
                {showAccount
                  ? bankDetails.accountNumber || "—"
                  : maskAccount(bankDetails.accountNumber)}
              </span>
            </div>
            <div className="meta-block">
              <span className="meta-label">Opened On</span>
              <span className="meta-value">
                {parseISODate(bankDetails.accountOpeningDate)}
              </span>
            </div>
          </div>

          <div className="quick-actions">
            <button className="ghost" onClick={() => setShowAccount((v) => !v)}>
              {showAccount ? "Hide Account Number" : "View Account Number"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
